// Pure logic for the flow dashboard (Stage 3): node order, tone, labels and
// where each item leads.
import { FD } from './flowDashboardStrings'
import type { FlowFlag, FlowNode, FlowNodeCounts, FlowNodeItem, FlowNodeKey, FlowSummary } from './visibilityTypes'

export const FLOW_ORDER: FlowNodeKey[] = ['catering_orders', 'plans', 'production', 'requisitions', 'store', 'purchasing']
export const FLAG_ORDER: FlowFlag[] = ['overdue', 'short', 'waiting', 'in_transit']

export type Tone = 'red' | 'amber' | 'green' | 'idle'

/** Red when something is late or short, amber when something waits, green otherwise. */
export function nodeTone(c: FlowNodeCounts | null | undefined): Tone {
  if (!c) return 'idle'
  if (c.overdue > 0 || c.short > 0) return 'red'
  if (c.waiting > 0) return 'amber'
  return 'green'
}

/**
 * A node's box colour. The server sends the tone (it sees the dates: a finished
 * short batch or a plan weeks away does not colour a box); the counts rule is
 * the fallback for a server that does not.
 */
export function boxTone(node: FlowNode | null | undefined): Tone {
  if (!node) return 'idle'
  return node.tone ?? nodeTone(node.counts)
}

export function needsAttention(c: FlowNodeCounts): number {
  return c.overdue + c.short + c.waiting
}

export function flagLabel(node: FlowNodeKey, flag: FlowFlag): string {
  return FD.flagsByNode[node]?.[flag] ?? FD.flags[flag]
}

/** The non-zero counts, worst first, for a node's box. */
export function visibleCounts(node: FlowNodeKey, c: FlowNodeCounts): { flag: FlowFlag; n: number; label: string }[] {
  return FLAG_ORDER.filter((f) => c[f] > 0).map((f) => ({ flag: f, n: c[f], label: flagLabel(node, f) }))
}

/** Nodes in flow order; a node the server did not send is drawn as an empty slot. */
export function orderedNodes(s: FlowSummary): { key: FlowNodeKey; node: FlowNode | null }[] {
  const byKey = new Map<FlowNodeKey, FlowNode>(s.nodes.map((n) => [n.key, n]))
  if (s.catering_orders) byKey.set('catering_orders', s.catering_orders)
  return FLOW_ORDER.map((key) => ({ key, node: byKey.get(key) ?? null }))
}

export interface DeepLink {
  to: string
  search?: Record<string, string>
}

/** Where a tap on an item (or a node) goes — always an existing screen. */
export function deepLink(node: FlowNodeKey, item?: FlowNodeItem): DeepLink {
  switch (node) {
    case 'plans':
    case 'production':
      return { to: '/admin/production' }
    case 'requisitions':
      return { to: '/admin/inventory', search: { tab: 'requests' } }
    case 'store':
      return item?.flag === 'short' ? { to: '/admin/inventory', search: { tab: 'purchasing' } } : { to: '/admin/inventory', search: { tab: 'requests' } }
    case 'purchasing':
      return { to: '/admin/inventory', search: { tab: 'purchasing' } }
    case 'catering_orders':
      return { to: '/admin/catering' }
    default:
      return { to: '/admin/kitchen-flow' }
  }
}

/** Items sorted worst first, stable within a flag. */
export function sortItems(items: FlowNodeItem[]): FlowNodeItem[] {
  return [...items].sort((a, b) => FLAG_ORDER.indexOf(a.flag) - FLAG_ORDER.indexOf(b.flag))
}
